import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import roleService from "@/services/api/roles";
import type { GetRolesParams, Role, RoleModulePermissions, RolePermissionsMatrixData } from "@/services/api/roles";

export interface RoleState {
  roles: Role[];
  totalRoles: number;
  isFetching: boolean;
  error: string | null;
  permissionsMatrix: RolePermissionsMatrixData | null;
  isFetchingPermissionsMatrix: boolean;
  permissionsMatrixError: string | null;
  editingRolePermissions: RoleModulePermissions[] | null;
  isFetchingEditingRolePermissions: boolean;
  editingRolePermissionsError: string | null;
}

const initialState: RoleState = {
  roles: [],
  totalRoles: 0,
  isFetching: false,
  error: null,
  permissionsMatrix: null,
  isFetchingPermissionsMatrix: false,
  permissionsMatrixError: null,
  editingRolePermissions: null,
  isFetchingEditingRolePermissions: false,
  editingRolePermissionsError: null,
};

export const getRoles = createAsyncThunk(
  "role/getRoles",
  async (params: GetRolesParams | undefined) => roleService.getRoles(params),
);

export const getRolePermissionsMatrix = createAsyncThunk(
  "role/getRolePermissionsMatrix",
  async () => roleService.getRolePermissionsMatrix(),
);

export const getRolePermissionsByRoleId = createAsyncThunk(
  "role/getRolePermissionsByRoleId",
  async (roleId: number) => roleService.getRolePermissionsByRoleId(roleId),
);

const roleSlice = createSlice({
  name: "role",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getRoles.pending, (state) => {
        state.isFetching = true;
        state.error = null;
      })
      .addCase(getRoles.fulfilled, (state, action) => {
        state.isFetching = false;
        state.roles = action.payload?.data?.results ?? [];
        state.totalRoles = action.payload?.data?.totalRecord ?? state.roles.length;
      })
      .addCase(getRoles.rejected, (state, action) => {
        state.isFetching = false;
        state.roles = [];
        state.error = action.error.message ?? "Failed to load roles";
      })
      .addCase(getRolePermissionsMatrix.pending, (state) => {
        state.isFetchingPermissionsMatrix = true;
        state.permissionsMatrixError = null;
      })
      .addCase(getRolePermissionsMatrix.fulfilled, (state, action) => {
        state.isFetchingPermissionsMatrix = false;
        state.permissionsMatrix = action.payload?.data ?? null;
      })
      .addCase(getRolePermissionsMatrix.rejected, (state, action) => {
        state.isFetchingPermissionsMatrix = false;
        state.permissionsMatrix = null;
        state.permissionsMatrixError = action.error.message ?? "Failed to load role permissions";
      })
      .addCase(getRolePermissionsByRoleId.pending, (state) => {
        state.isFetchingEditingRolePermissions = true;
        state.editingRolePermissionsError = null;
        state.editingRolePermissions = null;
      })
      .addCase(getRolePermissionsByRoleId.fulfilled, (state, action) => {
        state.isFetchingEditingRolePermissions = false;
        state.editingRolePermissions = action.payload?.data ?? null;
      })
      .addCase(getRolePermissionsByRoleId.rejected, (state, action) => {
        state.isFetchingEditingRolePermissions = false;
        state.editingRolePermissions = null;
        state.editingRolePermissionsError = action.error.message ?? "Failed to load role permissions";
      });
  },
});

export default roleSlice.reducer;
